import React from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import * as Icons from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Card, CardContent } from "~/components/ui/card";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import MobileSalesCard from "~/pages/purchase-orders/components/sales-notes/MobileSalesCard";
import PaymentBadge from "~/pages/purchase-orders/components/sales-notes/PaymentBadge";
import StatusBadge from "~/pages/purchase-orders/components/sales-notes/StatusBadge";
import { SalesTableProps } from "~/pages/purchase-orders/types/sales-notes";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";
import { getDisplayId } from "~/utils/salesRequestDisplayUtils";

export const SalesTable: React.FC<SalesTableProps> = ({
  displayedOrders,
  isLoading,
  getEmptyStateMessage,
  visibleColumns,
  clientNames,
  clientInfo,
  getSellerInfo,
  renderSalesCondition,
  salesRequestItems,
  handleOrderClick,
  isSeller,
}) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <TableSkeleton rows={8} columns={8} />
        </CardContent>
      </Card>
    );
  }

  if (displayedOrders.length === 0) {
    return <div className="p-8 text-center">{getEmptyStateMessage()}</div>;
  }

  // Check if we need to show the mobile card view (only for sellers on small screens)
  const isMobileSeller = isSeller && window.innerWidth < 768;

  return (
    <Card>
      <CardContent className="p-0">
        {isMobileSeller ? (
          <div className="grid grid-cols-1 gap-3 p-3">
            {displayedOrders.map((order) => (
              <MobileSalesCard
                key={order.id}
                order={order}
                clientNames={clientNames}
                clientInfo={clientInfo}
                handleOrderClick={handleOrderClick}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* For mobile, prioritize essential columns and hide others */}
                  {visibleColumns.id && (
                    <TableHead
                      className={isSeller ? "hidden md:table-cell" : ""}
                    >
                      ID
                    </TableHead>
                  )}
                  {visibleColumns.status && <TableHead>Estado</TableHead>}
                  {visibleColumns.client && <TableHead>Cliente</TableHead>}
                  {visibleColumns.seller && !isSeller && (
                    <TableHead
                      className={isSeller ? "hidden md:table-cell" : ""}
                    >
                      Vendedor
                    </TableHead>
                  )}
                  {visibleColumns.salesCondition && (
                    <TableHead className="hidden md:table-cell">
                      Condición
                    </TableHead>
                  )}
                  {visibleColumns.amount && (
                    <TableHead>Monto (con IVA)</TableHead>
                  )}
                  {visibleColumns.items && (
                    <TableHead className="hidden md:table-cell">
                      Productos
                    </TableHead>
                  )}
                  {visibleColumns.date && (
                    <TableHead className="hidden md:table-cell">
                      Fecha
                    </TableHead>
                  )}
                  {visibleColumns.paymentStatus && <TableHead>Pago</TableHead>}
                  {visibleColumns.pricingWarning && (
                    <TableHead className="hidden md:table-cell">
                      Alerta de Precio
                    </TableHead>
                  )}
                  {visibleColumns.facturado && (
                    <TableHead className="hidden md:table-cell">
                      Facturado
                    </TableHead>
                  )}
                  {visibleColumns.rejectedReason && (
                    <TableHead className="hidden md:table-cell">
                      Motivo de Rechazo
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedOrders.map((order, index) => {
                  const sellerInfo = getSellerInfo(order.createdBy);
                  return (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      onClick={() => handleOrderClick(order)}
                    >
                      {visibleColumns.id && (
                        <TableCell
                          className={`font-medium ${
                            isSeller ? "hidden md:table-cell" : ""
                          }`}
                        >
                          {getDisplayId(order)}
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>
                          <StatusBadge status={order.status} />
                        </TableCell>
                      )}
                      {visibleColumns.client && (
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Icons.User className="h-3 w-3 text-muted-foreground" />
                            <span>
                              {clientNames[order.clientId] ||
                                "Cliente sin nombre"}
                            </span>
                            {clientInfo?.[order.clientId]?.isDeleted && (
                              <Badge
                                variant="destructive"
                                className="ml-1 px-1 py-0 text-xs"
                              >
                                <Icons.Trash2 className="mr-1 h-3 w-3" />
                                Eliminado
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.seller && !isSeller && (
                        <TableCell>
                          {sellerInfo.name} {sellerInfo.lastname}
                        </TableCell>
                      )}
                      {visibleColumns.salesCondition && (
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1 text-sm">
                            <Icons.CreditCard className="h-3 w-3 text-muted-foreground" />
                            {renderSalesCondition(order)}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.amount && (
                        <TableCell>
                          {formatCurrency(
                            grossFromNet(order.totalAmount, order.isExento)
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.items && (
                        <TableCell className="hidden md:table-cell">
                          {salesRequestItems[order.id]?.length || 0} productos
                        </TableCell>
                      )}
                      {visibleColumns.date && (
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1 text-sm">
                            <Icons.Calendar className="h-3 w-3 text-muted-foreground" />
                            <span>
                              {format(new Date(order.createdAt), "dd/MM/yyyy", {
                                locale: es,
                              })}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.paymentStatus && (
                        <TableCell>
                          <PaymentBadge
                            paid={order.paid}
                            status={order.status}
                            previousStatus={order.previousStatus}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.pricingWarning && (
                        <TableCell className="hidden md:table-cell">
                          {order.hasPricingWarning ? (
                            <div className="flex items-center gap-1 text-sm text-amber-600">
                              <Icons.AlertTriangle className="h-3 w-3" />
                              <span>Sí</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              No
                            </span>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.facturado && (
                        <TableCell className="hidden md:table-cell">
                          {order.factura_creation_date ? (
                            <div className="flex items-center gap-1 text-sm text-green-600">
                              <Icons.CheckCircle className="h-3 w-3" />
                              <span>Sí</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              No
                            </span>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.rejectedReason && (
                        <TableCell className="hidden md:table-cell">
                          {order.rejectedReason ? (
                            <div className="flex items-center gap-1 text-sm text-red-600">
                              <Icons.AlertTriangle className="h-3 w-3" />
                              <span
                                className="max-w-[200px] truncate"
                                title={order.rejectedReason}
                              >
                                {order.rejectedReason}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesTable;
